import { useState } from 'react';

import { useProject } from '@contexts/Project.context';
import { useAuth } from '@contexts/User.context';
import { useToast } from '@contexts/Toast.context';
import {
	captureMapCanvas,
	captureMapCanvasAsBuffer,
} from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/utils';
import {
	generateDspPDFContent,
	exportDspToDOCX,
} from '@routes/userRoutes/projects/singleProject/components/modifyLayers/dspColorFilter/dspExportUtils';
import { prepareCollectionDspExportData } from '../utils/collectionDspExportData';

/**
 * PDF/DOCX report export for the collection DSP color-filter — the DSP
 * counterpart to `useHexagonExport`, minus geometry export (the plots' own
 * footprints are already downloadable from the project's footprints layer, so
 * there's no separate KML/GeoJSON here).
 *
 * Isolated from template/classification concerns: this hook only ever *reads*
 * the resolved classification (`results`), never computes or mutates it.
 *
 * @param {object} params
 * @param {object} params.results - per-class classification results
 * @param {object|null} params.filter - active filter config
 * @param {string} params.collectionName
 * @param {Array<{key: string, label: string}>} params.objects - collection detection objects
 * @param {string} params.projectNotes
 * @param {{id,symbol,label}} params.currency
 * @param {Array<{id,label,unit}>} params.availableProperties
 * @param {object|null} params.coverageGaps
 * @param {object|null} params.summary - whole-collection plot summary
 * @returns {{
 *   showExportModal: boolean, setShowExportModal: Function,
 *   exportTheme: string, setExportTheme: Function,
 *   isExporting: boolean,
 *   handleExport: (format: 'pdf'|'docx') => void,
 * }}
 */
const useDspExport = ({
	results,
	filter,
	collectionName,
	objects,
	projectNotes,
	currency,
	availableProperties,
	coverageGaps,
	summary,
}) => {
	const { mapObject, project } = useProject();
	const { currentUser } = useAuth();
	const { addToast } = useToast();

	const [showExportModal, setShowExportModal] = useState(false);
	const [exportTheme, setExportTheme] = useState('dark');
	const [isExporting, setIsExporting] = useState(false);

	const buildExportData = () =>
		prepareCollectionDspExportData({
			filter,
			classificationResults: results,
			collectionName,
			project,
			objects,
			userName: currentUser?.userName || currentUser?.email,
			projectNotes,
			currency,
			availableProperties,
			coverageGaps,
			summary,
		});

	const exportPDF = theme => {
		setIsExporting(true);
		try {
			const mapImageData = captureMapCanvas(mapObject);
			const htmlContent = generateDspPDFContent(buildExportData(), theme, mapImageData);

			const printWindow = window.open('', '_blank');
			if (!printWindow) {
				addToast({ title: 'Please allow popups for this site', bg: 'warning' });
				return;
			}
			printWindow.document.write(htmlContent);
			printWindow.document.close();
			addToast({ title: 'Report opened in new tab', bg: 'success' });
		} catch (err) {
			console.error('DSP PDF export error:', err);
			addToast({ title: 'Failed to generate report', bg: 'danger' });
		} finally {
			setIsExporting(false);
		}
	};

	const exportDOCX = async () => {
		setIsExporting(true);
		try {
			const mapImageBuffer = captureMapCanvasAsBuffer(mapObject);
			await exportDspToDOCX(buildExportData(), mapImageBuffer);
			addToast({ title: 'DOCX report downloaded', bg: 'success' });
		} catch (err) {
			console.error('DSP DOCX export error:', err);
			addToast({ title: 'Failed to generate DOCX report', bg: 'danger' });
		} finally {
			setIsExporting(false);
		}
	};

	const handleExport = format => {
		setShowExportModal(false);
		if (format === 'pdf') exportPDF(exportTheme);
		else if (format === 'docx') exportDOCX();
	};

	return {
		showExportModal,
		setShowExportModal,
		exportTheme,
		setExportTheme,
		isExporting,
		handleExport,
	};
};

export default useDspExport;
